import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, IconButton, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBilling } from '../../context/BillingContext';
import { choosePlanButtonSx } from './freeAccessNotice';
import { changePlanPath } from './billingPaths';
import { limitActionOf, limitButton } from './limitAction';
import apiClient from '../../services/apiClient';

// Interim limit modal (#248). The real barrier is the server-side 402 on
// message sends — this modal is pure UX, so it's now dismissible. Instead of
// dropping the user on the dead-end plans screen (no self-serve renewal exists
// yet), it lets them request a renewal, which emails the team.
//
// #759 D18: una prueba o un comp no piden renovación: eligen plan. Y cuando el backend ya ha
// cortado (`serverBlockedAt`), el aviso no se cierra: sin saldo no pueden hacer nada más, y la
// página de planes (donde este modal no sale) tiene su salida. Antes de ese corte sí se cierra,
// porque el 100 % que ve la interfaz va inflado un 10 % y llega antes que el corte real.
//
// #759 D15: la salida de una cuenta de pago (subir, contactar, soporte, avisar al admin) la decide
// `limitActionOf`, la misma que usa el aviso de «se ha parado por saldo».

const UsageLimitModal = () => {
  const { warningLevel, usage, blockSignalAt, serverBlockedAt } = useBilling();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const freeAccess = Boolean(usage?.freeAccess);

  const [dismissed, setDismissed] = useState(false);

  // Se reabre al entrar en el plan agotado o cuando el usuario lo pide (`blockSignalAt`). Los
  // choques siguientes con el tope los avisa `CreditStopNotice`, con la misma salida.
  useEffect(() => {
    if (blockSignalAt) setDismissed(false);
  }, [blockSignalAt]);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [error, setError] = useState(false);
  // A quién llegó la petición: 'org_managers' (los de su org) o 'team' (el equipo de AIFlock).
  const [notified, setNotified] = useState(null);

  // Hide on /billing/* routes (the modal would have nowhere useful to point).
  const onBillingRoute = location.pathname.startsWith('/billing');

  const planName = usage?.plan ?? '';

  const action = limitActionOf(usage);

  const send = async (path, body) => {
    setRequesting(true);
    setError(false);
    try {
      const res = await apiClient.post(path, body);
      setNotified(res?.notified ?? null);
      setRequested(true);
    } catch {
      setError(true);
    } finally {
      setRequesting(false);
    }
  };

  const handleAction = () => {
    if (action.kind === 'change') {
      setDismissed(true);
      navigate(changePlanPath(action.planId));
      return;
    }
    if (action.kind === 'checkout') {
      navigate('/billing/required');
      return;
    }
    if (action.kind === 'contact') {
      send('/api/billing/enterprise-interest', { planId: action.planId });
      return;
    }
    // renewal, notify_admin y support van por la misma ruta: el backend decide a quién avisar.
    send('/api/billing/request-renewal');
  };

  if (freeAccess) {
    const locked = Boolean(serverBlockedAt);
    return (
      <Dialog
        open={warningLevel === 100 && !onBillingRoute && (locked || !dismissed)}
        onClose={locked ? undefined : () => setDismissed(true)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}
        data-testid="free-access-limit-modal"
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1, pr: locked ? undefined : 5 }}>
          {t('billing.freeAccess.balanceExhausted', 'Has agotado el saldo de tu prueba')}
          {!locked && (
            <IconButton
              aria-label={t('billing.limit.closeAria', 'cerrar')}
              onClick={() => setDismissed(true)}
              sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('billing.limit.freeBody', 'Para seguir usando AIFlock, elige un plan. Tus agentes y tus conversaciones se quedan.')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/billing/required')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, ...choosePlanButtonSx(theme) }}
          >
            {t('billing.freeAccess.choosePlan', 'Elegir un plan')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={warningLevel === 100 && !onBillingRoute && !dismissed}
      onClose={() => setDismissed(true)}
      PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1, pr: 5 }}>
        {planName
          ? t('billing.limit.titleWithPlan', { plan: planName, defaultValue: 'Has alcanzado el límite de tu plan {{plan}}' })
          : t('billing.limit.title', 'Has alcanzado el límite de tu plan')}
        <IconButton
          aria-label={t('billing.limit.closeAria', 'cerrar')}
          onClick={() => setDismissed(true)}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" data-testid="usage-limit-body">
          {limitBody(t, action, requested, notified)}
        </Typography>
        {/* El bloqueo lee el % que agrega usageAggregatorJob cada 5 min: tras
            renovar, el acceso no vuelve al instante. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }} data-testid="usage-limit-refresh-note">
          {t('billing.limit.refreshNote', 'El consumo se recalcula cada 5 minutos: tras una renovación, el acceso puede tardar ese tiempo en volver.')}
        </Typography>
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
            {t('billing.limit.error', 'No se pudo enviar la solicitud. Inténtalo de nuevo.')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          variant="contained"
          fullWidth
          disabled={requesting || requested}
          onClick={handleAction}
          data-testid="usage-limit-action"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {requested
            ? t('billing.limit.sent', 'Solicitud enviada ✓')
            : requesting
              ? t('billing.limit.sending', 'Enviando…')
              : limitButton(t, action)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function limitBody(t, action, requested, notified) {
  if (requested) {
    if (action.kind === 'notify_admin' && notified === 'org_managers') {
      return t('billing.limit.adminNotified', 'Hemos avisado a quien gestiona la facturación de tu organización. Mientras tanto no podrás enviar nuevos mensajes.');
    }
    if (action.kind === 'contact') {
      return t('billing.limit.contactSent', 'Hemos recibido tu solicitud. Te escribiremos al correo de tu cuenta.');
    }
    return t('billing.limit.requested', 'Hemos recibido tu solicitud de renovación. Te contactaremos pronto. Mientras tanto no podrás enviar nuevos mensajes.');
  }
  switch (action.kind) {
    case 'change':
      return t('billing.limit.bodyChange', { plan: action.planName, defaultValue: 'Para seguir ahora, sube al plan {{plan}}: se cobra hoy y tu uso incluido vuelve a empezar con el del plan nuevo.' });
    case 'checkout':
      return t('billing.limit.freeBody', 'Para seguir usando AIFlock, elige un plan. Tus agentes y tus conversaciones se quedan.');
    case 'contact':
      return t('billing.limit.bodyContact', { plan: action.planName, defaultValue: 'El plan siguiente, {{plan}}, es de equipo y lo configuramos contigo. Escríbenos y te contactamos.' });
    case 'support':
      return t('billing.limit.bodySupport', 'Para ampliar tu plan, escríbenos y lo vemos contigo.');
    case 'notify_admin':
      return t('billing.limit.bodyMember', 'Quien gestiona la facturación de tu organización puede cambiar de plan. Avísale desde aquí.');
    default:
      return t('billing.limit.body', 'Para seguir usando AIFlock necesitas renovar tu plan. Solicítalo y nuestro equipo se pondrá en contacto contigo.');
  }
}

export default UsageLimitModal;
